import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button } from 'components/common';
import { setPaymentRequestFinalSigner, getPaymentRequest } from "redux/erp/paymentRequestSlice";
import SelectDropdown from "../common/SelectDropdown";
import UnderlineInput from "../common/UnderlineInput";
import Clickable from "../common/Clickable";

/**
 * Người ký cuối (BOD) của phiếu.
 *
 * Quyền ký thuộc về người giữ vai BOD trong cấu hình, nhưng thường ngày do người
 * được ủy quyền ký thay. Kế toán trưởng chọn một trong hai cho từng phiếu — mặc
 * định là người ký thường xuyên, chỉ đổi khi người đó bận, và phải ghi lý do.
 *
 * Chỉ hiện khi backend cho phép (`permissions.allow_set_final_signer`) hoặc khi
 * phiếu đã có người ký cuối được chỉ định (để mọi người cùng thấy dấu vết).
 */
const FinalSignerCard = ({ paymentRequest: pr }) => {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [userId, setUserId] = useState(pr?.final_signer?.id || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const options = pr?.final_signer_options || [];
  const canEdit = !!pr?.permissions?.allow_set_final_signer && options.length > 0;
  const current = pr?.final_signer;

  if (!canEdit && !current) return null;

  const defaultOption = options.find((o) => o.default);
  const picked = options.find((o) => String(o.id) === String(userId));
  const isStandIn = picked && !picked.default;

  const save = async () => {
    if (!userId) { toast.warning("Choose who signs this request."); return; }
    if (isStandIn && !reason.trim()) {
      toast.warning("Give a reason for using the stand-in signer.");
      return;
    }
    setSaving(true);
    try {
      await dispatch(setPaymentRequestFinalSigner({ id: pr.id, userId, reason: reason.trim() })).unwrap();
      await dispatch(getPaymentRequest(pr.id));
      toast.success("Final signer updated.");
      setEditing(false);
      setReason("");
    } catch (e) {
      toast.error(e?.error || e?.message || "Could not update the final signer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
      <div className="tw-px-5 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-flex tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <i className="bi bi-pen tw-text-geist-fg-3" />
          <span className="tw-text-sm tw-font-semibold tw-text-geist-fg">Final signer (BOD)</span>
        </div>
        {canEdit && !editing && (
          <Clickable onClick={() => { setUserId(current?.id || defaultOption?.id || ""); setEditing(true); }}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100">
            <i className="bi bi-arrow-left-right" /> {current ? "Change" : "Choose"}
          </Clickable>
        )}
      </div>

      <div className="tw-p-5">
        {!editing ? (
          current ? (
            <>
              <div className="tw-text-sm tw-font-semibold tw-text-geist-fg">{current.name}</div>
              <div className="tw-text-[13px] tw-text-geist-fg-3">{current.email}</div>
              {(current.set_by || current.reason) && (
                <div className="tw-mt-2.5 tw-text-[12px] tw-text-geist-fg-3">
                  {current.set_by && <>Chosen by {current.set_by}{current.set_at ? ` · ${current.set_at}` : ""}</>}
                  {current.reason && <div className="tw-mt-1 tw-italic">“{current.reason}”</div>}
                </div>
              )}
            </>
          ) : (
            <p className="tw-text-[13px] tw-text-geist-fg-3 tw-m-0">
              Not chosen yet — {defaultOption ? `${defaultOption.name} signs by default.` : "no BOD signer is configured."}
            </p>
          )
        ) : (
          <div className="tw-space-y-3">
            <SelectDropdown
              value={userId}
              options={options.map((o) => ({
                value: o.id,
                label: `${o.name}${o.default ? " — signs by default" : " — stand-in"}`,
              }))}
              onChange={setUserId}
              placeholder="Choose the final signer..."
            />

            {isStandIn && (
              <div>
                <div className="tw-text-[12px] tw-font-semibold tw-text-geist-fg-2 tw-mb-1">
                  Reason <span className="tw-text-geist-error">*</span>
                  <span className="tw-font-normal tw-text-geist-fg-3"> — why the stand-in signs this one</span>
                </div>
                <UnderlineInput value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. away on business until 20/09" />
              </div>
            )}

            <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-1">
              <Button variant="secondary" size="sm" disabled={saving}
                onClick={() => { setEditing(false); setReason(""); }}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={saving} onClick={save}>Save</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const idType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

FinalSignerCard.propTypes = {
  paymentRequest: PropTypes.shape({
    id: idType,
    final_signer: PropTypes.shape({
      id: idType,
      name: PropTypes.string,
      email: PropTypes.string,
      set_by: PropTypes.string,
      set_at: PropTypes.string,
      reason: PropTypes.string,
    }),
    final_signer_options: PropTypes.arrayOf(PropTypes.shape({
      id: idType,
      name: PropTypes.string,
      default: PropTypes.bool,
    })),
    permissions: PropTypes.shape({
      allow_set_final_signer: PropTypes.bool,
    }),
  }),
};

export default FinalSignerCard;
